import React, { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import PrimaryContactComp from "../../../PrimaryContactComp/PrimaryContactComp";
import { PrimaryContactCardLayout } from "./PrimaryContactCardLayout";
import {
  getPlainText,
  type TextNodeType,
} from "../../../FormattedText/FormattedText";
import type { ITravellerDetailsFormV2 } from "../../../AddTravellerDetail/type";
import { useTravellerDetailsContext } from "../../contexts/TravellerDetailsV2Context";

type ApiContactField = {
  fieldName?: string;
  isRequired?: boolean;
  isEnabled?: boolean;
  regex?: string;
  title?: unknown;
  value?: string;
  placeholder?: string;
  errorMessage?: string;
  countryCode?: string;
};

type PrimaryContactBlock = {
  title?: unknown;
  info?: unknown;
  isEditable?: boolean;
  fields?: {
    mobile?: ApiContactField;
    email?: ApiContactField;
  };
};

function fieldLabel(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  return getPlainText(v as TextNodeType);
}

function toPrimaryContactFieldProps(f: ApiContactField) {
  const rx = f.regex?.trim();
  return {
    countryCode: f.countryCode,
    errorMessage: f.errorMessage ?? "",
    placeholder: f.placeholder ?? "",
    fieldName: f.fieldName ?? "",
    isRequired: f.isRequired !== false,
    regex: rx && rx.length > 0 ? rx : ".*",
    title: fieldLabel(f.title),
    value: f.value ?? "",
    isEnabled: f.isEnabled !== false,
  };
}

export const PrimaryContactSection = ({
  primaryContact,
  note,
}: {
  primaryContact?: PrimaryContactBlock;
  note?: { title?: unknown; info?: unknown };
}) => {
  const {
    saveFormValuesOnBlur,
    handleCountryCodeModal,
    selectedCountry,
    isLoading,
    usePrimaryContactCardUi = true,
    primaryContactRef,
  } = useTravellerDetailsContext();

  const { setValue } = useFormContext<ITravellerDetailsFormV2>();

  const mobileVal = primaryContact?.fields?.mobile?.value ?? "";
  const emailVal = primaryContact?.fields?.email?.value ?? "";

  useEffect(() => {
    setValue("primaryContactData", {
      mobile: mobileVal,
      email: emailVal,
    });
  }, [mobileVal, emailVal, setValue]);

  const compPayload = useMemo(() => {
    if (!primaryContact) return null;
    const { title, info, isEditable, fields } = primaryContact;
    const mobile = fields?.mobile;
    const email = fields?.email;
    if (!mobile && !email) return null;

    return {
      title: title != null ? title : "",
      info: info != null ? info : "",
      isEditable: isEditable !== false,
      fields: {
        mobile: mobile ? toPrimaryContactFieldProps(mobile) : undefined,
        email: email ? toPrimaryContactFieldProps(email) : undefined,
      },
    };
  }, [primaryContact]);

  if (!compPayload) return null;

  return (
    <div style={{ padding: "0 16px" }} ref={primaryContactRef}>
      {usePrimaryContactCardUi ? (
        <PrimaryContactCardLayout
          primaryContact={
            compPayload as React.ComponentProps<
              typeof PrimaryContactCardLayout
            >["primaryContact"]
          }
          note={note}
          saveFormValuesOnBlur={saveFormValuesOnBlur}
          handleCountryCodeModal={handleCountryCodeModal}
          selectedCountry={selectedCountry}
          isLoading={isLoading}
        />
      ) : (
        <PrimaryContactComp
          primaryContact={
            compPayload as React.ComponentProps<
              typeof PrimaryContactComp
            >["primaryContact"]
          }
          setValue={setValue}
          note={note as React.ComponentProps<typeof PrimaryContactComp>["note"]}
          saveFormValuesOnBlur={saveFormValuesOnBlur}
          handleCountryCodeModal={handleCountryCodeModal}
          selectedCountry={selectedCountry}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};
